/**
 * <td-product-reviews> — PDP reviews section (sections/td-product-reviews.liquid).
 *
 * Loads review cards and the AI reviews summary from the Okendo storefront API,
 * paginates with the API's nextUrl, and opens Okendo's own review form through the
 * visually hidden Okendo reviews widget rendered inside the section.
 *
 * All API strings are written with textContent; nothing from the API goes through innerHTML.
 */
(() => {
  if (customElements.get('td-product-reviews')) return;

  const API_BASE = 'https://api.okendo.io/v1';
  const AUTO_RATING_TITLE = /^\d Stars?$/i;
  // Reviews longer than this get a 4-line clamp and a Read more toggle.
  const LONG_REVIEW_CHARS = 260;
  const WRITE_REVIEW_SELECTOR = '.oke-w-writeReview';

  /** Custom element for the PDP reviews section: review cards, pagination, AI summary, write a review. */
  class TdProductReviews extends HTMLElement {
    /**
     * Reads settings and elements, binds the buttons and starts loading reviews.
     * @returns {void}
     */
    connectedCallback() {
      this.productId = this.dataset.productId || '';
      this.userId = (this.dataset.okendoUserId || '').trim();
      this.limit = Math.min(10, Math.max(1, parseInt(this.dataset.limit, 10) || 3));
      this.strings = this.readStrings();

      this.list = this.querySelector('[data-td-review-list]');
      this.moreButton = this.querySelector('[data-td-show-more]');
      this.errorEl = this.querySelector('[data-td-review-error]');
      this.aiEl = this.querySelector('[data-td-ai-summary]');
      this.writeButton = this.querySelector('[data-td-write-review]');
      this.okendoWidget = this.querySelector('[data-td-okendo-widget]');

      this.controller = new AbortController();
      this.nextUrl = '';

      this.onWriteReview = this.onWriteReview.bind(this);
      this.onShowMore = this.onShowMore.bind(this);
      this.writeButton?.addEventListener('click', this.onWriteReview);
      this.moreButton?.addEventListener('click', this.onShowMore);

      if (!this.productId || !this.userId) {
        this.removeSkeletons();
        return;
      }

      if (this.list) this.loadFirstPage();
      if (this.aiEl && this.dataset.showAiSummary === 'true') this.loadAiSummary();
    }

    /**
     * Aborts pending requests and unbinds the buttons.
     * @returns {void}
     */
    disconnectedCallback() {
      this.controller?.abort();
      this.writeButton?.removeEventListener('click', this.onWriteReview);
      this.moreButton?.removeEventListener('click', this.onShowMore);
    }

    /**
     * Parses the section's data-strings JSON.
     * @returns {Record<string, string>} The UI strings, or an empty object if unreadable.
     */
    readStrings() {
      try {
        return JSON.parse(this.dataset.strings || '{}');
      } catch {
        return {};
      }
    }

    /**
     * Looks up a UI string and fills its [placeholder] tokens.
     * @param {string} key String key from the strings block.
     * @param {Record<string, string|number>} [replacements] Values for [placeholder] tokens.
     * @returns {string} The resolved string, or '' when the key is missing.
     */
    t(key, replacements = {}) {
      let str = this.strings[key] || '';
      Object.keys(replacements).forEach((k) => {
        str = str.replace(`[${k}]`, replacements[k]);
      });
      return str;
    }

    /**
     * Fetches a URL as JSON, aborted when the element disconnects.
     * @param {string} url Request URL.
     * @returns {Promise<any>} The parsed response body.
     */
    async fetchJson(url) {
      const res = await fetch(url, { signal: this.controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    }

    /**
     * Okendo API base path for this product.
     * @returns {string} The product's API URL.
     */
    productPath() {
      return `${API_BASE}/stores/${encodeURIComponent(this.userId)}/products/shopify-${encodeURIComponent(this.productId)}`;
    }

    /* ---------- Reviews ---------- */

    /**
     * Loads the first page of reviews, replacing the skeleton cards.
     * @returns {Promise<void>}
     */
    async loadFirstPage() {
      const url = `${this.productPath()}/reviews?limit=${this.limit}&orderBy=${encodeURIComponent(this.dataset.order || 'date desc')}`;
      try {
        const data = await this.fetchJson(url);
        this.removeSkeletons();
        const reviews = Array.isArray(data?.reviews) ? data.reviews : [];
        if (!reviews.length) {
          // No reviews is a valid answer (e.g. a stale count metafield): hide the list, no error.
          this.list.closest('.td-product-reviews__main')?.setAttribute('hidden', '');
          return;
        }
        // Replace, not append: connectedCallback runs again if the element is moved.
        this.list.replaceChildren();
        this.clearError();
        this.appendReviews(reviews);
        this.setNextUrl(data?.nextUrl);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.warn('td-product-reviews: failed to load reviews', err);
        this.removeSkeletons();
        this.showError();
      } finally {
        this.list?.setAttribute('aria-busy', 'false');
      }
    }

    /**
     * Loads the next page of reviews and focuses the first new card.
     * @returns {Promise<void>}
     */
    async onShowMore() {
      if (!this.nextUrl || this.moreButton.getAttribute('aria-busy') === 'true') return;
      this.moreButton.setAttribute('aria-busy', 'true');
      this.list.setAttribute('aria-busy', 'true');
      try {
        const data = await this.fetchJson(this.nextUrl);
        const reviews = Array.isArray(data?.reviews) ? data.reviews : [];
        const firstNew = this.appendReviews(reviews);
        this.setNextUrl(data?.nextUrl);
        this.clearError();
        if (firstNew) firstNew.focus({ preventScroll: false });
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.warn('td-product-reviews: failed to load more reviews', err);
        this.showError({ keepList: true });
      } finally {
        this.moreButton.removeAttribute('aria-busy');
        this.list.setAttribute('aria-busy', 'false');
      }
    }

    /**
     * Stores the next page URL and toggles the Show more button.
     * @param {unknown} nextUrl The API's nextUrl (path or absolute URL); anything else clears it.
     * @returns {void}
     */
    setNextUrl(nextUrl) {
      if (typeof nextUrl === 'string' && nextUrl.trim()) {
        const path = nextUrl.trim();
        if (/^https?:\/\//i.test(path)) {
          // Absolute URLs must stay on the Okendo API host.
          let sameHost = false;
          try {
            sameHost = new URL(path).origin === new URL(API_BASE).origin;
          } catch {
            sameHost = false;
          }
          this.nextUrl = sameHost ? path : '';
        } else {
          this.nextUrl = `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
        }
      } else {
        this.nextUrl = '';
      }
      if (this.moreButton) this.moreButton.hidden = !this.nextUrl;
    }

    /**
     * Removes the placeholder skeleton cards.
     * @returns {void}
     */
    removeSkeletons() {
      this.querySelectorAll('[data-td-skeleton]').forEach((el) => el.remove());
    }

    /**
     * Shows the load error message. Replaces the list unless keepList is set (Show more failed).
     * @param {{keepList?: boolean}} [options]
     * @returns {void}
     */
    showError({ keepList = false } = {}) {
      if (!this.errorEl) return;
      this.errorEl.textContent = this.t('error');
      if (this.list && !keepList) this.list.hidden = true;
    }

    /**
     * Hides the error message after a successful load.
     * @returns {void}
     */
    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
    }

    /**
     * Appends review cards. Returns the first new card (for focus management).
     * @param {Array<object>} reviews Okendo review objects.
     * @returns {HTMLElement|null} The first new card, or null when nothing was added.
     */
    appendReviews(reviews) {
      if (!this.list || !reviews.length) return null;
      const fragment = document.createDocumentFragment();
      let first = null;
      reviews.forEach((review) => {
        const card = this.buildCard(review);
        if (!first) first = card;
        fragment.appendChild(card);
      });
      this.list.hidden = false;
      this.list.appendChild(fragment);
      return first;
    }

    /**
     * Creates an element with an optional class and text content.
     * @param {string} tag Tag name.
     * @param {string} [className] Class attribute.
     * @param {string|number|null} [text] Text content (set with textContent).
     * @returns {HTMLElement} The new element.
     */
    el(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== undefined && text !== null) node.textContent = String(text);
      return node;
    }

    /**
     * Builds one review card.
     * @param {object} review Okendo review object.
     * @returns {HTMLElement} The card list item.
     */
    buildCard(review) {
      const card = this.el('li', 'td-product-reviews__card');
      card.tabIndex = -1;

      const rating = Math.max(0, Math.min(5, Number(review?.rating) || 0));
      const top = this.el('div', 'td-product-reviews__card-top');
      const stars = this.el('span', 'td-product-reviews__stars');
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', this.t('starsLabel', { rating }));
      stars.style.setProperty('--td-rating', String(rating));
      top.appendChild(stars);

      const dateText = this.formatDateAgo(review?.dateCreated);
      if (dateText) {
        const date = this.el('time', 'td-product-reviews__date', dateText);
        date.setAttribute('datetime', review.dateCreated);
        top.appendChild(date);
      }
      card.appendChild(top);

      const title = typeof review?.title === 'string' ? review.title.trim() : '';
      if (title && !AUTO_RATING_TITLE.test(title)) {
        card.appendChild(this.el('h3', 'td-product-reviews__title', title));
      }

      const body = typeof review?.body === 'string' ? review.body.trim() : '';
      if (body) {
        const bodyEl = this.el('p', 'td-product-reviews__body', body);
        card.appendChild(bodyEl);
        // Long reviews are clamped (CSS) to keep cards the design's height; the toggle shows the rest.
        if (body.length > LONG_REVIEW_CHARS) {
          bodyEl.classList.add('td-product-reviews__body--clamped');
          bodyEl.id = `td-review-body-${(review?.reviewId || Math.random().toString(36).slice(2)).toString().replace(/[^\w-]/g, '')}`;
          const toggle = this.el('button', 'td-product-reviews__read-more', this.t('readMore'));
          toggle.type = 'button';
          toggle.setAttribute('aria-expanded', 'false');
          toggle.setAttribute('aria-controls', bodyEl.id);
          toggle.addEventListener('click', () => {
            const expanded = bodyEl.classList.toggle('td-product-reviews__body--clamped') === false;
            toggle.setAttribute('aria-expanded', String(expanded));
            toggle.textContent = this.t(expanded ? 'readLess' : 'readMore');
          });
          card.appendChild(toggle);
        }
      }

      const reviewer = this.el('p', 'td-product-reviews__reviewer');
      const name = (review?.reviewer?.displayName || '').trim() || this.t('anonymous');
      reviewer.appendChild(this.el('span', 'td-product-reviews__name', name));
      if (review?.reviewer?.isVerified) {
        reviewer.appendChild(this.el('span', 'td-product-reviews__verified', this.t('verified')));
      }
      card.appendChild(reviewer);

      const tags = this.buildTags(review?.productAttributes);
      if (tags) card.appendChild(tags);

      return card;
    }

    /* ---------- Product attributes (Longevity, Sillage, …) ---------- */

    /**
     * Builds the product attribute tags list for a card.
     * @param {unknown} attributes The review's productAttributes.
     * @returns {HTMLElement|null} The tags list, or null when there is nothing to show.
     */
    buildTags(attributes) {
      if (!Array.isArray(attributes) || !attributes.length) return null;
      const items = attributes
        .map((attr) => ({ label: this.attributeLabel(attr?.title), value: this.attributeValue(attr) }))
        .filter((item) => item.label && item.value);
      if (!items.length) return null;

      const list = this.el('ul', 'td-product-reviews__tags');
      items.forEach((item) => {
        const li = this.el('li', 'td-product-reviews__tag');
        li.appendChild(this.el('span', 'td-product-reviews__tag-label', item.label));
        li.appendChild(this.el('span', 'td-product-reviews__tag-value', item.value));
        list.appendChild(li);
      });
      return list;
    }

    /**
     * "How is the longevity?" → "longevity" (uppercased in CSS).
     * @param {unknown} title Attribute title from the API.
     * @returns {string} The short label, or '' when the title is not a string.
     */
    attributeLabel(title) {
      if (typeof title !== 'string') return '';
      let label = title.trim();
      const match = label.match(/^how\s+is\s+(?:the\s+)?(.+?)\s*\??$/i);
      if (match) label = match[1];
      return label.replace(/\?+$/, '').trim();
    }

    /**
     * Okendo attribute values are a string (single choice), an array (multiple choice) or a
     * number (range / centred-range, labelled with minLabel / midLabel / maxLabel).
     * @param {object} attr Okendo product attribute.
     * @returns {string} The display value, or '' when it cannot be shown.
     */
    attributeValue(attr) {
      const value = attr?.value;
      if (typeof value === 'string') return value.trim();
      if (Array.isArray(value)) {
        return value.filter((v) => typeof v === 'string' && v.trim()).join(', ');
      }
      if (typeof value === 'number' && Number.isFinite(value)) {
        const min = attr.minLabel || '';
        const mid = attr.midLabel || '';
        const max = attr.maxLabel || '';
        if (!min && !mid && !max) return '';
        // Normalise to 0–1. Values above 1 are treated as a 1–5 scale.
        const n = value > 1 ? (Math.min(5, value) - 1) / 4 : Math.max(0, value);
        if (n <= 0.33) return min || mid;
        if (n >= 0.67) return max || mid;
        return mid || (n < 0.5 ? min : max);
      }
      return '';
    }

    /**
     * Formats a date as relative text ("3 months ago").
     * @param {string} dateStr ISO date string.
     * @returns {string} The relative date, or '' when the date is invalid.
     */
    formatDateAgo(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return '';
      const days = Math.floor((Date.now() - date.getTime()) / 86400000);
      const years = Math.floor(days / 365);
      const months = Math.floor(days / 30);
      if (years >= 1) return years === 1 ? this.t('yearAgo') : this.t('yearsAgo', { count: years });
      if (months >= 1) return months === 1 ? this.t('monthAgo') : this.t('monthsAgo', { count: months });
      if (days >= 1) return days === 1 ? this.t('dayAgo') : this.t('daysAgo', { count: days });
      return this.t('today');
    }

    /* ---------- AI summary ---------- */

    /**
     * Loads the AI reviews summary; stays hidden when it is missing or fails.
     * @returns {Promise<void>}
     */
    async loadAiSummary() {
      try {
        const data = await this.fetchJson(`${this.productPath()}/reviews_summary`);
        const body = data?.reviewsSummary?.body;
        const text = typeof body === 'string' ? body.trim() : '';
        if (!text) return;
        const bodyEl = this.aiEl.querySelector('[data-td-ai-summary-body]');
        if (!bodyEl) return;
        bodyEl.textContent = text;
        this.aiEl.hidden = false;
      } catch {
        // Quietly stay hidden: the summary is optional.
      }
    }

    /* ---------- Write a review ---------- */

    /**
     * Finds Okendo's own Write a review button.
     * @returns {HTMLElement|null} The button, or null when Okendo has not rendered it.
     */
    findOkendoWriteButton() {
      return (
        this.okendoWidget?.querySelector(WRITE_REVIEW_SELECTOR) ||
        document.querySelector(WRITE_REVIEW_SELECTOR)
      );
    }

    /**
     * Opens Okendo's review form, or reveals the widget when it is not ready.
     * @returns {void}
     */
    onWriteReview() {
      const okendoButton = this.findOkendoWriteButton();
      if (okendoButton) {
        // The widget is inert while hidden; lift it just for this click so the activation goes through.
        const widget = this.okendoWidget;
        if (widget) widget.inert = false;
        okendoButton.click();
        if (widget && !widget.classList.contains('td-product-reviews__okendo--revealed')) widget.inert = true;
        return;
      }
      // Okendo isn't ready: show its widget (and make it interactive) and scroll to it.
      if (this.okendoWidget) {
        this.okendoWidget.inert = false;
        this.okendoWidget.classList.add('td-product-reviews__okendo--revealed');
        this.okendoWidget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  customElements.define('td-product-reviews', TdProductReviews);
})();
